import { compareAsc, isSameMinute, toDate } from 'date-fns'

import {
  PATIENT_STATUS_NAMES,
  PATIENT_STATUSES,
} from 'features/Home/constants/infoConstants'
import type { DisabledInterval, IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import { InfoService } from 'features/Home/services/infoService'

const formatTime = (time: string) => {
  const [hours, minutes] = time.split(':').map((item) => parseInt(item, 10))
  const formattedHours = hours.toString().padStart(2, '0')
  const formattedMinutes = minutes.toString().padStart(2, '0')
  return `${formattedHours}:${formattedMinutes}`
}

const getMinMaxHour = (timeInterval: { min: string; max: string }) => {
  const { min, max } = timeInterval
  if (!min || !max) return { minHour: '', maxHour: '' }
  return { minHour: formatTime(min), maxHour: formatTime(max) }
}

/** Pure disabled-interval computation for clinic schedule day view (no Redux side effects). */
export const computeScheduleDayDisabledIntervals = (
  day: string,
  receivedRooms: IPatientRoom[],
  timeInterval: { min: string; max: string },
): DisabledInterval[] => {
  const { minHour, maxHour } = getMinMaxHour(timeInterval)
  if (!minHour || !maxHour) return []

  const currentDate = new Date(`${day} ${minHour}`)

  return receivedRooms
    .map((room: IPatientRoom) => {
      const sortedSlots = InfoService.getSortedSlots(room.slots, day)
      const updatedSlots = InfoService.getDisabledSlotsBasedOnSortedSlots(sortedSlots)

      if (!updatedSlots.length) {
        return [
          {
            id: `${room.value}-full`,
            name: room.label,
            start: toDate(currentDate),
            end: toDate(new Date(`${day} ${maxHour}`)),
            resourceId: `${room.label}/${room.value}`,
            status: {
              code: PATIENT_STATUSES.DISABLED,
              name: PATIENT_STATUS_NAMES[PATIENT_STATUSES.DISABLED],
            },
            isDraggable: false,
          },
        ]
      }

      const getEndDate = (index: number) => {
        if (updatedSlots[index + 1]?.start_time) {
          return toDate(updatedSlots[index + 1]?.start_time)
        }
        return toDate(new Date(`${day} ${maxHour}`))
      }

      const slotsDisabledIntervals = updatedSlots.map((slot, index) => ({
        id: `${room.value}-slot-${index}`,
        name: room.label,
        start: toDate(slot.end_time),
        end: getEndDate(index),
        resourceId: `${room.label}/${room.value}`,
        status: {
          code: PATIENT_STATUSES.DISABLED,
          name: PATIENT_STATUS_NAMES[PATIENT_STATUSES.DISABLED],
        },
        isDraggable: false,
      }))

      if (compareAsc(updatedSlots[0]?.start_time, currentDate) > 0) {
        slotsDisabledIntervals.unshift({
          id: `${room.value}-before-first`,
          name: room.label,
          start: toDate(currentDate),
          end: toDate(updatedSlots[0].start_time),
          resourceId: `${room.label}/${room.value}`,
          status: {
            code: PATIENT_STATUSES.DISABLED,
            name: PATIENT_STATUS_NAMES[PATIENT_STATUSES.DISABLED],
          },
          isDraggable: false,
        })
      }

      if (
        slotsDisabledIntervals.length > 0 &&
        isSameMinute(
          slotsDisabledIntervals[slotsDisabledIntervals.length - 1].start,
          slotsDisabledIntervals[slotsDisabledIntervals.length - 1].end,
        )
      ) {
        slotsDisabledIntervals.pop()
      }

      return slotsDisabledIntervals
    })
    .flat()
}
